/**
 * `LineSeries` — up to eleven year series on one plot, with an optional average line. UC2's
 * Trends tab (design `2026-10-02-uc2-tabs-analytics-design.md` §Tabs): the watersheds with
 * positive change against the rest, and — once the reader has clicked one — that
 * watershed's own series drawn **bold** over the two class means.
 *
 * Eleven rather than three since the client round of 3 Oct 2026: the Trends tab also draws
 * one line per watershed (or one per country), which is the whole programme on one plot.
 * The legend wraps, the palette is muted, and the moment any series is `highlight`ed the
 * rest are thinned and faded — a reader asked about one unit, and eleven equal lines would
 * answer with none.
 *
 * Hand-built SVG in the same register as `BarSeries`/`DimensionBars` (the repo forbids
 * chart libraries): viewBox-scaled, `role="img"` with a full plain-text readout carrying
 * every series, every year and the unit, so the chart is readable by a screen reader and
 * assertable by a test without measuring pixels.
 *
 * The y domain includes zero by default, for `BarSeries`' reason: a share or a trend is a
 * quantity, and an axis cut to the data's own range would exaggerate every year-to-year
 * wobble the captions are deliberately cautious about. A series that never goes near zero
 * and is read for its *differences* rather than its size — annual rainfall in millimetres,
 * where two flat lines at the top of a 0–900 plot say nothing — passes
 * `zeroBaseline={false}`. Either way the two ends of the domain are printed on the plot,
 * so a reader is never left guessing what the height of a line means.
 *
 * Pure and translation-free: every string — the aria label, the series names, the average's
 * name, the unit — is passed in already localised.
 */
import { fmtNum } from '../../../lib/format';

/** Chart width, matched to the Analysis dock's own body width. */
export const LS_W = 394;
/** Plot height, above the year labels. */
export const LS_PLOT_H = 84;
/** Room under the plot for the year labels. */
export const LS_LABEL_H = 14;
export const LS_PAD_TOP = 8;
export const LS_PAD_X = 6;
export const LS_H = LS_PAD_TOP + LS_PLOT_H + LS_LABEL_H;

/**
 * The fallback palette, for a caller that passes no `color` on a series: a green, a
 * neutral grey and a blue, in that order. Every UC2 caller passes its own colours from
 * `uc2Constants`, so this is only the chart refusing to draw two lines in one ink.
 */
export const LS_COLORS = ['#2a9d4a', '#9aa5a0', '#2B6CB0'] as const;

/**
 * How many series one plot carries at the dock's width, and a hard cap: `series` is
 * `slice`d to this, so a twelfth line **is** dropped, silently, and neither the plot nor
 * the readout mentions it.
 *
 * Eleven is the programme's own watershed count, which is why the cap has never bitten —
 * the per-unit charts pass exactly that many. It is a guard against a plot that cannot be
 * read, not a feature: a caller with more series than this needs a different chart, and
 * if one ever appears the honest fix is for the cap to say so on the plot rather than for
 * this number to creep upwards.
 */
export const LS_MAX_SERIES = 11;
const MEAN_COLOR = 'var(--ink-3)';

export interface LineSeriesPoint {
  year: number;
  value: number | null;
}

export interface LineSeriesLine {
  /** Stable key and `data-line` value — the class id, or the selected unit's id. */
  id: string;
  /** What the legend prints: a class name, or the selected watershed's name. */
  label: string;
  points: LineSeriesPoint[];
  color?: string;
  /** The line the panel is about — drawn bold, and named first in the readout. */
  highlight?: boolean;
}

export interface LineSeriesProps {
  /** Up to `LS_MAX_SERIES` series; anything past that is dropped rather than drawn. */
  series: LineSeriesLine[];
  ariaLabel: string;
  /** Drawn as a dashed line across the plot, named with its own value, and in the readout. */
  mean?: number | null;
  meanLabel?: string;
  unit?: string;
  decimals?: number;
  /**
   * Whether zero is forced into the y domain (default `true`). `false` scales to the data
   * itself — for a quantity whose interesting part is the gap between the lines, not the
   * distance from nothing.
   */
  zeroBaseline?: boolean;
  /** `data-testid` of the `<svg>`, so a panel with three charts can address each one. */
  testId?: string;
}

/** One point's own text — shared by the readout and the `<title>`, so they cannot disagree. */
function valueText(value: number | null, unit: string, decimals: number): string {
  return value == null ? '—' : `${fmtNum(value, decimals)}${unit ? ` ${unit}` : ''}`;
}

/** Every year any series carries, once, in order — the x axis of the plot. */
function yearsOf(series: LineSeriesLine[]): number[] {
  const seen = new Set<number>();
  for (const line of series) for (const p of line.points) seen.add(p.year);
  return [...seen].sort((a, b) => a - b);
}

export function LineSeries({
  series,
  ariaLabel,
  mean = null,
  meanLabel,
  unit = '',
  decimals = 1,
  zeroBaseline = true,
  testId = 'line-series-chart',
}: LineSeriesProps) {
  const lines = series.slice(0, LS_MAX_SERIES);
  /**
   * Once one line is the subject, the rest are context: thinner and faded, so the focused
   * unit reads off a plot of eleven. With nothing focused every line keeps its own weight.
   */
  const anyHighlight = lines.some((line) => line.highlight);
  const years = yearsOf(lines);
  /**
   * Each series as `year → value`, so the plot walks the **union** of years rather than
   * each series' own list: a year another series carries and this one does not breaks the
   * line exactly as an explicit `null` does, instead of being silently bridged.
   */
  const byYear = lines.map((line) => {
    const map = new Map<number, number>();
    for (const p of line.points) {
      if (typeof p.value === 'number' && Number.isFinite(p.value)) map.set(p.year, p.value);
    }
    return map;
  });
  const values: number[] = [];
  for (const map of byYear) values.push(...map.values());
  if (typeof mean === 'number' && Number.isFinite(mean)) values.push(mean);
  if (zeroBaseline) values.push(0);
  const lo = values.length ? Math.min(...values) : 0;
  const hi = values.length ? Math.max(...values) : 1;
  const span = hi - lo || 1;
  const plotW = LS_W - 2 * LS_PAD_X;
  const baseY = LS_PAD_TOP + LS_PLOT_H;
  const xOf = (i: number) =>
    years.length > 1 ? LS_PAD_X + (i / (years.length - 1)) * plotW : LS_PAD_X + plotW / 2;
  const yOf = (v: number) => baseY - ((v - lo) / span) * LS_PLOT_H;

  const readout =
    `${ariaLabel} — ` +
    lines
      .map(
        (line, li) =>
          `${line.label}: ` +
          years
            .map((year) => `${year} ${valueText(byYear[li]!.get(year) ?? null, unit, decimals)}`)
            .join(', '),
      )
      .join('. ') +
    (typeof mean === 'number' && Number.isFinite(mean)
      ? `. ${meanLabel ?? 'mean'} ${valueText(mean, unit, decimals)}`
      : '');
  const meanY = typeof mean === 'number' && Number.isFinite(mean) ? yOf(mean) : null;

  return (
    <>
      <svg
        className="intel-chart line-series"
        viewBox={`0 0 ${LS_W} ${LS_H}`}
        role="img"
        aria-label={readout}
        data-testid={testId}
      >
        {lines.map((line, li) => {
          const color = line.color ?? LS_COLORS[li % LS_COLORS.length];
          const map = byYear[li]!;
          // A year with no measured value breaks the line rather than being bridged: a
          // straight segment over a gap would be the chart asserting a number nobody
          // measured.
          const runs: string[] = [];
          let current: string[] = [];
          for (const [i, year] of years.entries()) {
            const value = map.get(year);
            if (value == null) {
              if (current.length > 1) runs.push(current.join(' '));
              current = [];
              continue;
            }
            current.push(`${current.length ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(value).toFixed(1)}`);
          }
          if (current.length > 1) runs.push(current.join(' '));
          const last = years.reduce<number | null>((seen, year) => map.get(year) ?? seen, null);
          return (
            <g key={line.id} className={line.highlight ? 'ls-line on' : 'ls-line'} data-line={line.id}>
              <title>{`${line.label} — ${valueText(last, unit, decimals)}`}</title>
              {runs.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={line.highlight ? 2.6 : anyHighlight ? 1 : 1.4}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity={line.highlight ? 1 : anyHighlight ? 0.45 : 0.9}
                />
              ))}
              {years.map((year, i) => {
                const value = map.get(year);
                if (value == null) return null;
                return (
                  <circle
                    key={year}
                    cx={xOf(i)}
                    cy={yOf(value)}
                    r={line.highlight ? 2.2 : anyHighlight ? 1 : 1.4}
                    fill={color}
                    opacity={line.highlight || !anyHighlight ? 1 : 0.45}
                  />
                );
              })}
            </g>
          );
        })}
        {meanY != null ? (
          <>
            <line
              data-mean=""
              x1={LS_PAD_X}
              x2={LS_W - LS_PAD_X}
              y1={meanY}
              y2={meanY}
              stroke={MEAN_COLOR}
              strokeWidth={1.5}
              strokeDasharray="3,2"
            />
            {meanLabel ? (
              <text
                className="ls-meanlabel"
                x={LS_W - LS_PAD_X}
                y={Math.max(8, meanY - 2)}
                textAnchor="end"
                fontSize="9"
                fill={MEAN_COLOR}
              >
                {`${meanLabel} ${valueText(mean, unit, decimals)}`}
              </text>
            ) : null}
          </>
        ) : null}
        <text className="ls-domain" x={LS_PAD_X} y={LS_PAD_TOP + 5} textAnchor="start" fontSize="9">
          {valueText(hi, unit, decimals)}
        </text>
        <text className="ls-domain" x={LS_PAD_X} y={baseY - 2} textAnchor="start" fontSize="9">
          {valueText(lo, unit, decimals)}
        </text>
        {years.map((year, i) => (
          <text
            key={year}
            className="ls-label"
            x={xOf(i)}
            y={baseY + LS_LABEL_H - 4}
            /*
             * The first and last points sit `LS_PAD_X` (6) from the edges, which is less
             * than half a four-digit year at this size — centred, both of them were cut
             * in half by the viewBox ("`017`", "`202`"), and the two years a reader needs
             * most on a trend chart are exactly the first and the last. They are anchored
             * to the inside instead; everything between stays centred on its point.
             */
            textAnchor={i === 0 ? 'start' : i === years.length - 1 ? 'end' : 'middle'}
            fontSize="10"
          >
            {year}
          </text>
        ))}
      </svg>
      <ul className="chart-legend">
        {lines.map((line, li) => (
          <li key={line.id} className={line.highlight ? 'on' : undefined}>
            <span
              className="swatch"
              style={{ background: line.color ?? LS_COLORS[li % LS_COLORS.length] }}
            />
            <span className="legend-label">{line.label}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export default LineSeries;
